import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveChats, markActiveChatsDelivered } from './chatApi';
import { startChatConnection, onChatConnected, joinChat, leaveChat } from './chatHub';
import { reportDelivered } from './receipts';
import { setActiveChats, chatMessageIncoming } from './redux';
import { withChatBoundary } from './ChatErrorBoundary';
import { senderType } from './chatState';

const reloadDelayMs = 300;
// Смяната на темата от координатора и готовият отговор след нея (системни събития 9 и 7).
const incomingSystemEvents = [7, 9];

// Непрочетено за техника (както my/active-chats в сървиса): от координатор — по роля, и когато е същият човек; от колега
// от екипа; смяната на темата от координатора с готовия отговор.
const isIncoming = (message, userId) =>
  message.senderType === senderType.coordinator ||
  (message.senderType === senderType.technician && message.senderUserId !== userId) ||
  (message.senderType === senderType.system && incomingSystemEvents.includes(message.systemEvent));

// Държи незавършените чатове на техника — от тях са индикаторите на бутоните и на картите.
// При свързване хъбът слага техника в групите на днешните му графици, затова събитията идват без абониране.
// Незавършените чатове се следят и когато не са отворени — за отметката „получено" при координатора.
const ActiveChatsLoader = () => {
  const dispatch = useDispatch();
  const enabled = useSelector(state => state.chat.enabled);
  const user = useSelector(state => state.auth.user);
  const userIdRef = useRef(null);
  userIdRef.current = user && user.id;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let connected = false;
    let timer = null;
    let connection = null;
    // Статусът и последният писал на всеки незавършен чат при последното зареждане — съобщенията също идват като
    // ChatUpdated (от групата на чата); презарежда се само при смяна на хода (червено/зелено), не на всяко.
    const known = new Map();
    const stateOf = chat => `${chat.status}:${chat.lastSenderType}`;
    const followed = new Set();

    const follow = chatId => {
      if (followed.has(chatId)) return;
      followed.add(chatId);
      joinChat(chatId);
    };
    const unfollow = chatId => {
      if (followed.delete(chatId)) leaveChat(chatId);
    };

    const load = () =>
      getActiveChats()
        .then(list => {
          if (cancelled) return;
          const chats = Array.isArray(list) ? list : [];
          dispatch(setActiveChats(chats));
          known.clear();
          chats.forEach(x => known.set(x.id, stateOf(x)));
          chats.forEach(x => follow(x.id));
          [...followed].filter(id => !known.has(id)).forEach(unfollow);
        })
        .catch(() => true);
    const markDelivered = () => markActiveChatsDelivered().catch(() => true);
    // Едно действие в чата праща няколко събития — презареждаме веднъж.
    const reloadSoon = () => {
      clearTimeout(timer);
      timer = setTimeout(load, reloadDelayMs);
    };
    const onChatUpdated = chat => {
      if (known.get(chat.id) !== stateOf(chat)) reloadSoon();
    };
    const onMessageAdded = message => {
      if (!followed.has(message.chatId)) return;
      reportDelivered(message.chatId, message.id);
      if (isIncoming(message, userIdRef.current)) dispatch(chatMessageIncoming(message));
    };

    load();
    startChatConnection()
      .then(c => {
        if (cancelled) return;
        connection = c;
        connected = true;
        c.on('ChatCreated', reloadSoon);
        c.on('ChatUpdated', onChatUpdated);
        c.on('MessageAdded', onMessageAdded);
        markDelivered();
      })
      .catch(() => true);
    const unsubscribe = onChatConnected(() => {
      if (!connected) return;
      load();
      markDelivered();
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      unsubscribe();
      followed.forEach(chatId => leaveChat(chatId));
      if (connection) {
        connection.off('ChatCreated', reloadSoon);
        connection.off('ChatUpdated', onChatUpdated);
        connection.off('MessageAdded', onMessageAdded);
      }
    };
  }, [dispatch, enabled]);

  return null;
};

export default withChatBoundary(ActiveChatsLoader);
